import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Mail, ArrowLeft, Sparkles, Check, X, Shield } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCoreAuth } from '../contexts/CoreAuthContext';
import { OtpVerificationForm } from '../components/OtpVerificationForm';

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { resetPassword } = useAuth();
  const { resetPasswordWithOtp, verifyOtp } = useCoreAuth();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [useOtp, setUseOtp] = useState(false);
  const [showOtpVerification, setShowOtpVerification] = useState(false);
  const [otpEmail, setOtpEmail] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      if (useOtp) {
        // Use OTP for password reset
        await resetPasswordWithOtp(email);
        setOtpEmail(email);
        setShowOtpVerification(true);
      } else {
        // Use traditional URL-based reset
        await resetPassword(email);
        setSuccess(true);
      }
    } catch (err: any) {
      console.error('Password reset error:', err);
      
      // Handle specific error messages
      if (err.message?.includes('User not found')) {
        setError('No account found with this email address');
      } else if (err.message?.includes('rate limit')) {
        setError('Too many requests. Please wait a few minutes before trying again.');
      } else {
        setError(useOtp ? 'Failed to send verification code. Please try again.' : 'Failed to send reset email. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpVerify = async (token: string) => {
    try {
      await verifyOtp(otpEmail, token, 'recovery');
      // After OTP verification, redirect to reset password page
      navigate('/reset-password');
    } catch (error: any) {
      throw new Error(error.message || 'Invalid verification code. Please try again.');
    }
  };

  const handleOtpResend = async () => {
    try {
      await resetPasswordWithOtp(otpEmail);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to resend verification code. Please try again.');
    }
  };

  if (showOtpVerification) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl p-8 w-full max-w-md shadow-2xl"
        >
          <button
            onClick={() => {
              setShowOtpVerification(false);
              setError(null);
            }}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back</span>
          </button>

          <OtpVerificationForm
            email={otpEmail}
            onVerify={handleOtpVerify}
            onResend={handleOtpResend}
            isLoading={isLoading}
            error={error}
            type="recovery"
            title="Reset Your Password"
            subtitle="Enter the verification code to continue"
          />
        </motion.div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl p-8 w-full max-w-md shadow-2xl text-center"
        >
          <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8 text-white" />
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Check Your Email
          </h2>
          
          <p className="text-gray-600 mb-6">
            We've sent password reset instructions to <strong>{email}</strong>
          </p>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-700">
              Click the link in your email to reset your password. The link will expire in 1 hour.
            </p>
          </div>
          
          <p className="text-sm text-gray-500 mb-6">
            Didn't receive the email? Check your spam folder or try again.
          </p>
          
          <button
            onClick={() => navigate('/onboarding?mode=login')}
            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all transform hover:scale-105"
          >
            Back to Sign In
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center p-6">
      <Helmet>
        <title>Reset Password - Rulevia</title>
      </Helmet>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-2xl p-8 w-full max-w-md shadow-2xl"
      >
        <button
          onClick={() => navigate('/onboarding?mode=login')}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back to Sign In</span>
        </button>

        <div className="text-center mb-8">
          <div className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4">
            <img
              src="/Logo-checkmark-sidebar.svg"
              alt="Rulevia Logo"
              className="w-12 h-12"
            />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Forgot Your Password?
          </h2>
          <p className="text-gray-600">
            No worries! Enter your email and we'll send you reset instructions.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError(null);
                }}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Enter your email"
                required
              />
            </div>
          </div>

          <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              id="useOtp"
              checked={useOtp}
              onChange={(e) => setUseOtp(e.target.checked)}
              className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 focus:ring-2"
            />
            <div className="flex-1">
              <label htmlFor="useOtp" className="flex items-center space-x-2 text-sm font-medium text-gray-700 cursor-pointer">
                <Shield className="w-4 h-4 text-purple-600" />
                <span>Use verification code instead of email link</span>
              </label>
              <p className="text-xs text-gray-500 mt-1">
                {useOtp 
                  ? 'Enter a 6-digit code sent to your email to reset your password'
                  : 'Click a link in your email to reset your password'
                }
              </p>
            </div>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2"
            >
              <X className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-red-700">{error}</span>
            </motion.div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {isLoading ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Sending...</span>
              </div>
            ) : (
              <span>{useOtp ? 'Send Verification Code' : 'Send Reset Instructions'}</span>
            )}
          </button>

          <p className="text-center text-sm text-gray-600 mt-4">
            Remember your password?{' '}
            <button
              type="button"
              onClick={() => navigate('/onboarding?mode=login')}
              className="text-purple-600 hover:text-purple-700 font-medium"
            >
              Sign in
            </button>
          </p>
        </form>
      </motion.div>
    </div>
  );
}